/**
 * The home page's sections, described once (ADR-056, ADR-057).
 *
 * The API validates a saved layout against this catalog, the admin draws its
 * "Add section" picker and every section's form from it, and the storefront
 * reads config through it — so the three can no longer disagree about what a
 * slideshow holds. The words an operator sees for a core section (a type's
 * name, a field's label) are the admin's copy, not this file's: the API has
 * no use for them.
 *
 * A theme describes its own sections the same way, with its own words
 * (`ThemeHomeSections`, ADR-057): the types it adds, the fields it adds to a
 * core type it draws, and the core fields it does not draw. `homeSectionCatalog`
 * merges the two. A type in neither is kept, listed and moved like any other,
 * but only its theme knows what is inside it.
 */
export const HOME_SECTION_VISIBILITIES = ['all', 'mobile', 'desktop'];
export const HOME_LAYOUT_LIMITS = {
    sections: 40,
    /** A section's config, as JSON characters — theme sections included. */
    configCharacters: 16_000,
    heading: 120,
    line: 240,
    paragraph: 2_000,
    label: 40,
    alt: 160,
    link: 500,
    /** A path or storage key naming an image. */
    image: 300,
    slug: 200,
};
const L = HOME_LAYOUT_LIMITS;
/** The icons a store promise may wear — drawn by the storefront and the admin alike. */
export const SECTION_ICONS = [
    'truck',
    'return',
    'shield',
    'cash',
    'card',
    'gift',
    'star',
    'chat',
    'clock',
    'tag',
    'scissors',
    'box',
];
// Field builders: the same field reads the same in every section that has it.
const heading = (name = 'title') => ({ name, kind: 'bilingual', max: L.heading });
const line = (name) => ({ name, kind: 'bilingual', max: L.line });
const label = (name) => ({ name, kind: 'bilingual', max: L.label });
const alt = { name: 'alt', kind: 'bilingual', max: L.alt };
const link = (name, fallback) => ({ name, kind: 'link', ...(fallback ? { default: fallback } : {}) });
const count = (name, max, fallback) => ({ name, kind: 'number', min: 1, max, default: fallback });
const height = { name: 'height', kind: 'select', options: ['small', 'medium', 'large'], default: 'medium' };
const align = (fallback) => ({
    name: 'align',
    kind: 'select',
    options: ['start', 'center', 'end'],
    default: fallback,
});
/** Light words for a dark photograph, dark words for a light one. */
const tone = { name: 'tone', kind: 'select', options: ['light', 'dark'], default: 'light' };
const layout = (fallback) => ({
    name: 'layout',
    kind: 'select',
    options: ['carousel', 'grid'],
    default: fallback,
});
/** The listing sorts a category's rail may take — each one `GET /storefront/products` serves. */
export const RAIL_CATEGORY_SORTS = ['featured', 'newest', 'bestselling', 'rating', 'price_asc', 'price_desc'];
/** Where a product rail's products come from. */
export const RAIL_SOURCES = ['category', 'picked', 'bestsellers', 'top_rated', 'newest', 'price_asc'];
export const HOME_SECTIONS = [
    // ─── Banners ─────────────────────────────────────────────────────────────
    {
        type: 'banner',
        group: 'banners',
        summary: 'text',
        fields: [{ ...line('text'), required: true }, link('href')],
    },
    {
        type: 'hero',
        group: 'banners',
        // The page's one headline (<h1>).
        maxPerPage: 1,
        summary: 'title',
        fields: [
            label('eyebrow'),
            heading(),
            line('subtitle'),
            label('ctaLabel'),
            link('ctaHref', '/products'),
            label('secondaryLabel'),
            link('secondaryHref'),
            { name: 'imageUrl', kind: 'image' },
            { name: 'mobileImageUrl', kind: 'image' },
        ],
    },
    {
        type: 'slideshow',
        group: 'banners',
        maxPerPage: 3,
        fields: [
            { name: 'autoplay', kind: 'toggle', default: true },
            { name: 'interval', kind: 'number', min: 3, max: 12, default: 6, when: { field: 'autoplay', is: ['true'] } },
            height,
        ],
        items: {
            name: 'slides',
            min: 1,
            max: 8,
            fields: [
                { name: 'image', kind: 'image', required: true },
                { name: 'mobileImage', kind: 'image' },
                alt,
                label('eyebrow'),
                heading(),
                line('subtitle'),
                label('ctaLabel'),
                link('href'),
                align('start'),
                tone,
            ],
        },
        preset: { slides: [{}] },
    },
    {
        type: 'image-banner',
        group: 'banners',
        summary: 'title',
        fields: [
            { name: 'image', kind: 'image', required: true },
            { name: 'mobileImage', kind: 'image' },
            alt,
            label('eyebrow'),
            heading(),
            line('subtitle'),
            label('ctaLabel'),
            link('href'),
            height,
            align('center'),
            tone,
        ],
    },
    {
        type: 'promo-grid',
        group: 'banners',
        summary: 'title',
        fields: [
            heading(),
            { name: 'columns', kind: 'select', options: ['2', '3', '4'], default: '3' },
            { name: 'aspect', kind: 'select', options: ['portrait', 'square', 'landscape'], default: 'portrait' },
        ],
        items: {
            name: 'tiles',
            min: 1,
            max: 6,
            fields: [{ name: 'image', kind: 'image', required: true }, alt, heading(), line('subtitle'), link('href')],
        },
        preset: { tiles: [{}, {}, {}] },
    },
    // ─── Products ────────────────────────────────────────────────────────────
    {
        type: 'product-rail',
        group: 'products',
        summary: 'title',
        fields: [
            heading(),
            { name: 'source', kind: 'select', options: RAIL_SOURCES, default: 'newest' },
            { name: 'categorySlug', kind: 'category', required: true, when: { field: 'source', is: ['category'] } },
            { name: 'sort', kind: 'select', options: RAIL_CATEGORY_SORTS, default: 'featured', when: { field: 'source', is: ['category'] } },
            { name: 'productIds', kind: 'products', max: 48, required: true, when: { field: 'source', is: ['picked'] } },
            count('limit', 24, 8),
            // A rail saved before carousels existed keeps its grid.
            layout('grid'),
            { name: 'viewAll', kind: 'toggle', default: true },
        ],
        preset: { source: 'bestsellers', layout: 'carousel' },
    },
    {
        type: 'featured-collection',
        group: 'products',
        summary: 'title',
        fields: [heading(), { name: 'categorySlug', kind: 'category', required: true }, count('limit', 24, 8), layout('grid')],
    },
    // ─── Categories ──────────────────────────────────────────────────────────
    {
        type: 'categories',
        group: 'categories',
        summary: 'title',
        fields: [
            heading(),
            // Empty is the top level, in the store's own order.
            { name: 'categorySlugs', kind: 'categories', max: 24 },
            count('limit', 24, 6),
            layout('grid'),
            { name: 'showCounts', kind: 'toggle', default: true },
        ],
    },
    // ─── Content ─────────────────────────────────────────────────────────────
    {
        type: 'features',
        group: 'content',
        summary: 'title',
        fields: [heading()],
        items: {
            name: 'features',
            min: 1,
            max: 6,
            fields: [
                { name: 'icon', kind: 'icon', default: 'truck' },
                { name: 'title', kind: 'bilingual', max: 60, required: true },
                line('text'),
            ],
        },
        preset: { features: [{ icon: 'truck' }, { icon: 'return' }, { icon: 'shield' }] },
    },
    {
        type: 'content-block',
        group: 'content',
        summary: 'title',
        fields: [
            label('eyebrow'),
            heading(),
            { name: 'body', kind: 'bilingualText', max: L.paragraph },
            label('ctaLabel'),
            link('ctaHref'),
            { name: 'align', kind: 'select', options: ['start', 'center'], default: 'start' },
        ],
    },
    {
        type: 'looks',
        group: 'content',
        flag: 'looks',
        summary: 'title',
        fields: [heading(), count('limit', 12, 2)],
    },
    {
        type: 'newsletter',
        group: 'content',
        flag: 'newsletter',
        maxPerPage: 1,
        summary: 'title',
        fields: [heading(), line('text')],
    },
];
export const HOME_SECTION_TYPES = HOME_SECTIONS.map((section) => section.type);
const CORE_CATALOG = new Map(HOME_SECTIONS.map((section) => [section.type, section]));
/**
 * The catalog with a theme's changes (ADR-057): the fields it adds to a core
 * type appended, the ones it does not draw taken out, and its own types after
 * the core's. A hidden field is not deleted from anyone's config — it is only
 * no longer asked for, nor checked. A theme type may not reuse a core name:
 * redrawing a core type is what `extend` and `hide` are for.
 */
export function homeSectionCatalog(theme) {
    if (!theme)
        return CORE_CATALOG;
    const catalog = new Map();
    for (const section of HOME_SECTIONS) {
        const hidden = new Set(theme.hide?.[section.type] ?? []);
        const extension = theme.extend?.[section.type] ?? [];
        const added = extension.filter((field) => !section.fields.some((own) => own.name === field.name));
        // A theme's words for a core field it draws; the field's kind and limits stay the core's.
        const reworded = new Map(extension.filter((field) => !added.includes(field)).map((field) => [field.name, field]));
        const worded = (field) => {
            const words = reworded.get(field.name);
            if (!words || words.kind !== field.kind)
                return field;
            return {
                ...field,
                ...(words.label ? { label: words.label } : {}),
                ...(words.hint ? { hint: words.hint } : {}),
                ...(words.placeholder ? { placeholder: words.placeholder } : {}),
                ...(words.optionLabels ? { optionLabels: words.optionLabels } : {}),
                ...(words.default !== undefined ? { default: words.default } : {}),
            };
        };
        catalog.set(section.type, {
            ...section,
            fields: [...section.fields.filter((field) => !hidden.has(field.name)).map(worded), ...added],
        });
    }
    for (const section of theme.types ?? []) {
        if (!catalog.has(section.type))
            catalog.set(section.type, { ...section, group: 'theme' });
    }
    return catalog;
}
/** A type's description — the core's, or the theme's when the catalog has it — or undefined for a type nobody described. */
export function homeSectionDefinition(type, catalog = CORE_CATALOG) {
    return catalog.get(type);
}
/**
 * A field as the catalog describes it: the section's own first, then its
 * items' — a slide's `tone` is found through the slideshow's type.
 */
export function homeSectionField(type, name, catalog = CORE_CATALOG) {
    const definition = catalog.get(type);
    return definition?.fields.find((field) => field.name === name) ?? definition?.items?.fields.find((field) => field.name === name);
}
/** The value a field reads as while nothing is written in it. */
export function sectionFieldDefault(type, name, catalog = CORE_CATALOG) {
    return homeSectionField(type, name, catalog)?.default;
}
/**
 * Whether a field is asked for in this config: `categorySlug` only while the
 * rail's source is a category. A select or toggle nobody touched counts as its
 * default, so a new rail asks the right questions before anything is chosen.
 */
export function sectionFieldApplies(field, config, fields) {
    if (!field.when)
        return true;
    const controller = fields.find((candidate) => candidate.name === field.when.field);
    const raw = config[field.when.field] ?? controller?.default;
    return raw !== undefined && raw !== null && field.when.is.includes(String(raw));
}
/** A new section's config: its fields' defaults, then its preset. */
export function newSectionConfig(type, catalog = CORE_CATALOG) {
    const definition = catalog.get(type);
    if (!definition)
        return {};
    const config = {};
    for (const field of definition.fields) {
        // Selects and toggles start where they say; words start empty.
        if (field.default !== undefined && (field.kind === 'select' || field.kind === 'toggle' || field.kind === 'number')) {
            config[field.name] = field.default;
        }
    }
    // A copy, so two sections added from one preset never share a slide.
    return JSON.parse(JSON.stringify({ ...config, ...(definition.preset ?? {}) }));
}
/**
 * A section's items as they read today: its list, or — for a config written
 * before the list existed — the plain entries of its legacy list, each as an
 * item. What the editor shows and what a theme draws are the same thing.
 */
export function readSectionItems(config, items) {
    const list = config[items.name];
    if (Array.isArray(list)) {
        return list.filter((item) => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
    }
    const legacy = items.legacy ? config[items.legacy.from] : undefined;
    if (items.legacy && Array.isArray(legacy)) {
        const field = items.legacy.field;
        return legacy.filter((entry) => entry !== null && entry !== undefined && entry !== '').map((entry) => ({ [field]: entry }));
    }
    return [];
}
// ─── A theme's description ───────────────────────────────────────────────────
const FIELD_KINDS = [
    'bilingual',
    'bilingualText',
    'number',
    'toggle',
    'select',
    'image',
    'video',
    'link',
    'category',
    'categories',
    'products',
    'icon',
];
const FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;
function isBilingual(value) {
    const pair = value;
    return Boolean(pair) && typeof pair?.ar === 'string' && typeof pair?.en === 'string';
}
function fieldProblem(field, where) {
    if (!field || typeof field !== 'object')
        return `${where} is not a field`;
    if (typeof field.name !== 'string' || !FIELD_NAME.test(field.name) || field.name === 'key')
        return `${where} needs a name of letters and digits (not "key")`;
    if (!FIELD_KINDS.includes(field.kind))
        return `${where} (${field.name}) has no kind this platform knows`;
    if (field.kind === 'select' && (!Array.isArray(field.options) || field.options.length === 0 || field.options.some((option) => typeof option !== 'string'))) {
        return `${where} (${field.name}) is a select with no options`;
    }
    for (const words of [field.label, field.hint, field.placeholder]) {
        if (words !== undefined && !isBilingual(words))
            return `${where} (${field.name}) has words that are not { ar, en }`;
    }
    return null;
}
/**
 * Why a theme's description cannot be used, or null. A theme is code the
 * store did not write; its description is checked before the admin draws a
 * form from it or the API checks a save against it.
 */
export function themeSectionsProblem(value) {
    const theme = value;
    if (!theme || typeof theme !== 'object')
        return 'not a description';
    if (!isBilingual(theme.name))
        return 'the theme needs a name in both languages';
    const types = theme.types ?? [];
    if (!Array.isArray(types) || types.length > 30)
        return 'types must be a list of at most 30';
    const seen = new Set();
    for (const [index, section] of types.entries()) {
        const where = `type ${index + 1}`;
        if (!section || typeof section.type !== 'string' || !TYPE_NAME.test(section.type))
            return `${where} needs a type name`;
        if (CORE_CATALOG.has(section.type))
            return `${where} (${section.type}) is a core type: extend or hide its fields instead`;
        if (seen.has(section.type))
            return `${where} (${section.type}) is described twice`;
        seen.add(section.type);
        if (!isBilingual(section.label))
            return `${where} (${section.type}) needs a label in both languages`;
        if (!Array.isArray(section.fields) || section.fields.length > 40)
            return `${where} (${section.type}) needs a list of at most 40 fields`;
        for (const [position, field] of section.fields.entries()) {
            const problem = fieldProblem(field, `${where} (${section.type}) field ${position + 1}`);
            if (problem)
                return problem;
        }
        if (section.items) {
            const items = section.items;
            if (typeof items.name !== 'string' || !FIELD_NAME.test(items.name))
                return `${where} (${section.type}) has items with no name`;
            if (!Number.isInteger(items.min) || !Number.isInteger(items.max) || items.min < 0 || items.max < Math.max(1, items.min) || items.max > 24) {
                return `${where} (${section.type}) items need a min and max between 0 and 24`;
            }
            if (!Array.isArray(items.fields))
                return `${where} (${section.type}) items need fields`;
            for (const words of [items.label, items.add, items.placeholder]) {
                if (words !== undefined && !isBilingual(words))
                    return `${where} (${section.type}) items have words that are not { ar, en }`;
            }
            for (const [position, field] of items.fields.entries()) {
                const problem = fieldProblem(field, `${where} (${section.type}) item field ${position + 1}`);
                if (problem)
                    return problem;
            }
        }
    }
    for (const [type, fields] of Object.entries(theme.extend ?? {})) {
        const core = CORE_CATALOG.get(type);
        if (!core)
            return `extend names ${type}, which is not a core type`;
        if (!Array.isArray(fields))
            return `extend.${type} must be a list of fields`;
        for (const [position, field] of fields.entries()) {
            const problem = fieldProblem(field, `extend.${type} field ${position + 1}`);
            if (problem)
                return problem;
            const own = core.fields.find((candidate) => candidate.name === field.name);
            if (own && own.kind !== field.kind)
                return `extend.${type} field ${position + 1} (${field.name}) rewords a core ${own.kind} field as a ${field.kind}`;
        }
    }
    for (const [type, names] of Object.entries(theme.hide ?? {})) {
        if (!CORE_CATALOG.has(type))
            return `hide names ${type}, which is not a core type`;
        if (!Array.isArray(names) || names.some((name) => typeof name !== 'string'))
            return `hide.${type} must list field names`;
    }
    return null;
}
// ─── Schedule and visibility ─────────────────────────────────────────────────
/** Whether a section shows at this moment: switched on, and inside its window when it has one. */
export function isSectionLive(block, now) {
    if (!block.enabled)
        return false;
    const startsAt = parseInstant(block.schedule?.startsAt);
    const endsAt = parseInstant(block.schedule?.endsAt);
    if (startsAt !== null && now.getTime() < startsAt)
        return false;
    if (endsAt !== null && now.getTime() >= endsAt)
        return false;
    return true;
}
function parseInstant(value) {
    if (typeof value !== 'string' || !value)
        return null;
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
}
// ─── Values ──────────────────────────────────────────────────────────────────
/** A storage key — `products/2026/….webp` — never a path out of the store. */
const STORAGE_KEY = /^(?!.*\.\.)[A-Za-z0-9][A-Za-z0-9/_.-]*$/;
const SECTION_ID = /^[A-Za-z0-9_-]{1,40}$/;
const TYPE_NAME = /^[a-z0-9][a-z0-9-]{0,39}$/;
/** A path on the store, or an https address. Nothing that runs, nothing protocol-relative. */
export function isSectionLink(value) {
    if (typeof value !== 'string' || value.length > L.link || /\s/.test(value))
        return false;
    if (value.startsWith('/'))
        return !value.startsWith('//') && !value.startsWith('/\\');
    return /^https:\/\/[^/]/i.test(value);
}
/**
 * An image or a film as stored: `{ key }` (the admin reads `{ key, url }`). A
 * path or URL written before ADR-056 is a plain string and still reads; so is
 * what the public read hands a theme.
 */
export function sectionImageKey(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const record = value;
        // Only `key` and its `url` beside it: an item that happens to have a field
        // called `key` is not a file.
        if (Object.keys(record).some((name) => name !== 'key' && name !== 'url'))
            return null;
        return typeof record.key === 'string' && record.key ? record.key : null;
    }
    return null;
}
function isImageValue(value) {
    const key = sectionImageKey(value);
    if (key !== null)
        return key.length <= L.image && STORAGE_KEY.test(key);
    return typeof value === 'string' && value.length <= L.image && (value.startsWith('/') ? !value.startsWith('//') : /^https?:\/\//i.test(value));
}
function isEmpty(value) {
    if (value === undefined || value === null || value === '')
        return true;
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'object') {
        if (sectionImageKey(value) !== null)
            return false;
        return Object.values(value).every((part) => typeof part !== 'string' || part.trim() === '');
    }
    return false;
}
/** Copy trimmed, empty languages dropped, nothing at all as undefined. A plain string reads in both languages. */
function tidyBilingual(value) {
    if (typeof value === 'string')
        return value.trim() || undefined;
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return value;
    const pair = value;
    const tidy = {};
    for (const locale of ['ar', 'en']) {
        const text = pair[locale];
        if (typeof text === 'string' && text.trim())
            tidy[locale] = text.trim();
    }
    return Object.keys(tidy).length > 0 ? tidy : undefined;
}
function tidyValue(field, value) {
    switch (field.kind) {
        case 'bilingual':
        case 'bilingualText':
            return tidyBilingual(value);
        case 'image':
        case 'video': {
            // What leaves the admin carries the url it showed; only the key is stored.
            const key = sectionImageKey(value);
            if (key !== null)
                return { key };
            return typeof value === 'string' ? value.trim() || undefined : value;
        }
        case 'link':
        case 'category':
        case 'icon':
        case 'select':
            return typeof value === 'string' ? value.trim() || undefined : value;
        case 'number':
            return value === null || value === '' ? undefined : value;
        default:
            return value;
    }
}
function tidyConfig(fields, config) {
    const tidy = { ...config };
    for (const field of fields) {
        if (!(field.name in tidy))
            continue;
        const value = tidyValue(field, tidy[field.name]);
        if (value === undefined)
            delete tidy[field.name];
        else
            tidy[field.name] = value;
    }
    return tidy;
}
/**
 * Checks and tidies a layout against the catalog. Pure: it knows no database,
 * so the admin can run it on every keystroke and the API before it asks
 * whether the categories named exist.
 */
export function checkHomeLayout(input, catalog = CORE_CATALOG) {
    const problems = [];
    const categorySlugs = new Set();
    const ids = new Set();
    const perType = new Map();
    const blocks = [];
    if (input.length > L.sections)
        problems.push({ section: L.sections, code: 'too_many_sections', limit: L.sections });
    input.forEach((raw, section) => {
        const entry = raw;
        if (!entry || typeof entry !== 'object' || typeof entry.type !== 'string' || typeof entry.enabled !== 'boolean') {
            problems.push({ section, code: 'not_a_section' });
            return;
        }
        const type = entry.type;
        const shown = entry.enabled;
        const at = (problem) => problems.push({ section, type, ...problem });
        if (!TYPE_NAME.test(type))
            at({ code: 'bad_type' });
        if (entry.id !== undefined) {
            if (typeof entry.id !== 'string' || !SECTION_ID.test(entry.id))
                at({ code: 'bad_id' });
            else if (ids.has(entry.id))
                at({ code: 'duplicate_id' });
            else
                ids.add(entry.id);
        }
        if (entry.visibility !== undefined && !HOME_SECTION_VISIBILITIES.includes(entry.visibility))
            at({ code: 'bad_visibility' });
        if (entry.schedule !== undefined && entry.schedule !== null) {
            const startsAt = entry.schedule.startsAt ? Date.parse(entry.schedule.startsAt) : null;
            const endsAt = entry.schedule.endsAt ? Date.parse(entry.schedule.endsAt) : null;
            if (Number.isNaN(startsAt) || Number.isNaN(endsAt) || (startsAt !== null && endsAt !== null && endsAt <= startsAt)) {
                at({ code: 'bad_schedule' });
            }
        }
        const config = entry.config && typeof entry.config === 'object' && !Array.isArray(entry.config) ? entry.config : {};
        if (JSON.stringify(config).length > L.configCharacters)
            at({ code: 'config_too_large', limit: L.configCharacters });
        const definition = catalog.get(type);
        const seen = (perType.get(type) ?? 0) + 1;
        perType.set(type, seen);
        if (definition?.maxPerPage !== undefined && seen === definition.maxPerPage + 1) {
            at({ code: 'too_many_of_type', limit: definition.maxPerPage });
        }
        let tidy = config;
        if (definition) {
            tidy = tidyConfig(definition.fields, config);
            checkFields(definition.fields, tidy, shown, categorySlugs, (problem) => at(problem));
            if (definition.items) {
                const items = definition.items;
                const list = tidy[items.name] ?? (items.legacy && Array.isArray(tidy[items.legacy.from]) ? readSectionItems(tidy, items) : undefined);
                if (list !== undefined && !Array.isArray(list)) {
                    at({ field: items.name, code: 'bad_value' });
                }
                else {
                    // An item with nothing in it is a card someone added and never filled.
                    // Each keeps the place it had in the list it came from, so a problem
                    // points at the card the editor shows, empty ones included.
                    const kept = (list ?? [])
                        .map((item, position) => ({
                        position,
                        item: item && typeof item === 'object' && !Array.isArray(item) ? tidyConfig(items.fields, item) : null,
                    }))
                        .filter((entry) => entry.item !== null && items.fields.some((field) => !isEmpty(entry.item[field.name])));
                    tidy = { ...tidy, [items.name]: kept.map((entry) => entry.item) };
                    // Read from an older plain list, the section is stored in the new shape alone.
                    if (items.legacy && tidy[items.name] !== config[items.name])
                        delete tidy[items.legacy.from];
                    if (kept.length > items.max)
                        at({ field: items.name, code: 'too_many', limit: items.max });
                    if (shown && kept.length < items.min)
                        at({ field: items.name, code: 'too_few', limit: items.min });
                    kept.forEach((entry) => checkFields(items.fields, entry.item, shown, categorySlugs, (problem) => at({ ...problem, item: entry.position })));
                }
            }
        }
        blocks.push({
            ...(entry.id !== undefined ? { id: entry.id } : {}),
            type,
            enabled: shown,
            sortOrder: blocks.length + 1,
            config: tidy,
            ...(entry.visibility && entry.visibility !== 'all' ? { visibility: entry.visibility } : {}),
            ...(entry.schedule && (entry.schedule.startsAt || entry.schedule.endsAt)
                ? { schedule: { startsAt: entry.schedule.startsAt || null, endsAt: entry.schedule.endsAt || null } }
                : {}),
        });
    });
    return { blocks, problems, categorySlugs };
}
function checkFields(fields, config, shown, categorySlugs, report) {
    for (const field of fields) {
        const value = config[field.name];
        const applies = sectionFieldApplies(field, config, fields);
        if (isEmpty(value)) {
            if (field.required && shown && applies)
                report({ field: field.name, code: 'required' });
            continue;
        }
        const at = (code, limit) => report({ field: field.name, code, ...(limit !== undefined ? { limit } : {}) });
        switch (field.kind) {
            case 'bilingual':
            case 'bilingualText': {
                const max = field.max ?? L.line;
                const parts = typeof value === 'string' ? [value] : typeof value === 'object' && !Array.isArray(value) ? Object.values(value) : null;
                if (!parts || parts.some((part) => typeof part !== 'string'))
                    at('bad_value');
                else if (parts.some((part) => part.length > max))
                    at('too_long', max);
                break;
            }
            case 'number':
                if (typeof value !== 'number' || !Number.isInteger(value))
                    at('bad_value');
                else if ((field.min !== undefined && value < field.min) || (field.max !== undefined && value > field.max))
                    at('out_of_range');
                break;
            case 'toggle':
                if (typeof value !== 'boolean')
                    at('bad_value');
                break;
            case 'select':
                if (typeof value !== 'string' || !(field.options ?? []).includes(value))
                    at('not_an_option');
                break;
            case 'image':
            case 'video':
                if (!isImageValue(value))
                    at('bad_image');
                break;
            case 'link':
                if (!isSectionLink(value))
                    at('bad_link');
                break;
            case 'icon':
                if (typeof value !== 'string' || !SECTION_ICONS.includes(value))
                    at('bad_icon');
                break;
            case 'category':
                if (typeof value !== 'string' || value.length > L.slug || /[\s/]/.test(value))
                    at('bad_value');
                // Only a shown section's categories must exist: a hidden one is a draft,
                // and is checked again the day it is switched on and saved.
                else if (applies && shown)
                    categorySlugs.add(value);
                break;
            case 'categories':
            case 'products': {
                const list = Array.isArray(value) ? value : null;
                if (!list || list.some((part) => typeof part !== 'string' || !part || part.length > L.slug))
                    at('bad_value');
                else if (field.max !== undefined && list.length > field.max)
                    at('too_many', field.max);
                else if (new Set(list).size !== list.length)
                    at('bad_value');
                else if (field.kind === 'categories' && applies && shown)
                    for (const slug of list)
                        categorySlugs.add(slug);
                break;
            }
        }
    }
}
/** A problem as a sentence, for the API's refusal: "section 3 (slideshow), item 2: image is required". */
export function describeHomeLayoutProblem(problem) {
    const where = [
        `section ${problem.section + 1}${problem.type ? ` (${problem.type})` : ''}`,
        problem.item !== undefined ? `item ${problem.item + 1}` : null,
    ]
        .filter(Boolean)
        .join(', ');
    const what = problem.field ?? 'it';
    const messages = {
        too_many_sections: `a page holds at most ${problem.limit} sections`,
        not_a_section: 'needs a string "type" and a boolean "enabled"',
        bad_id: 'its id must be 1–40 letters, digits, dashes or underscores',
        duplicate_id: 'its id is used by another section',
        bad_type: 'its type must be lower-case letters, digits and dashes',
        too_many_of_type: `a page holds at most ${problem.limit} of this section`,
        bad_visibility: 'visibility must be all, mobile or desktop',
        bad_schedule: 'its schedule must be dates, the end after the start',
        config_too_large: `its settings are over ${problem.limit} characters`,
        required: `${what} is required`,
        too_long: `${what} is over ${problem.limit} characters`,
        out_of_range: `${what} is out of range`,
        not_an_option: `${what} is not one of its options`,
        too_many: `${what} holds at most ${problem.limit}`,
        too_few: `${what} needs at least ${problem.limit}`,
        bad_value: `${what} is not the right kind of value`,
        bad_link: `${what} must be a path on the store or an https address`,
        bad_image: `${what} is not an uploaded image`,
        bad_icon: `${what} is not one of the icons`,
    };
    return problem.code === 'too_many_sections' ? messages.too_many_sections : `${where}: ${messages[problem.code]}`;
}
/**
 * Every uploaded file a layout holds — an image or a film, stored as `{ key }`
 * — replaced by what `map` makes of it: the API turns keys into URLs for the
 * storefront, and into `{ key, url }` for the admin. Found wherever it sits,
 * so a theme's sections are served the same way as the core's without anyone
 * here knowing their fields. `map` returning undefined drops the file.
 */
export function mapHomeLayoutImages(blocks, map) {
    const walk = (value) => {
        if (sectionImageKey(value) !== null)
            return map(value);
        if (Array.isArray(value))
            return value.map(walk).filter((entry) => entry !== undefined);
        if (value && typeof value === 'object') {
            const next = {};
            for (const [name, entry] of Object.entries(value)) {
                const mapped = walk(entry);
                if (mapped !== undefined)
                    next[name] = mapped;
            }
            return next;
        }
        return value;
    };
    return blocks.map((block) => block.config && typeof block.config === 'object' ? { ...block, config: walk(block.config) } : block);
}
//# sourceMappingURL=home-sections.js.map